import moment from 'moment';

import config from './config';

import { bookingStatus } from 'types/booking';
import { types as paymentTypes, codes } from 'constants/payment';
import { productFamilyCodes } from 'types/productFamilyCodes';
import productTypes from 'constants/productTypes';
import serviceTypeCodes from 'constants/serviceTypeCodes';

const generateMerchantReference = (bookingReference) => `${bookingReference}_${moment().utcOffset(0).format('YYYYMMDDHHmmssSS')}`;

// VOY-4121
// This needs to be added as part of a condition for through-fare / summated-fare,
// Otherwise subset of passenger upgrades will lead to booking being evaluated as summated-fares
// see definition: https://eurostarwiki.atlassian.net/wiki/spaces/VOY/pages/4764401701/Voyager+Through-fares+VS.+Summated+fares
const uniquePassengerInMultipleLegs = ({ direction }) => {
  const allPassengerIds = direction
    .flatMap(({ products }) => products.filter(({ cancelled }) => !cancelled))
    ?.map(({ passengerId }) => passengerId);
  const uniquePassengerIds = [...new Set(allPassengerIds)];
  return allPassengerIds.length === uniquePassengerIds.length;
};

// Indirect journey (i.e. legs.length > 1)
// Same passenger id is found in different legs (i.e. a passenger has a 2 legs journey)
// then either through fare or summated fare
const isThroughFareOrSummatedFares = ({ direction }) =>
  direction.length > 1 &&
  !uniquePassengerInMultipleLegs({ direction }) &&
  direction.some(({ isThroughFare, isSummatedFare }) => isThroughFare || isSummatedFare);

const getAvailablePaymentTypes = ({ totalPriceToBePaid, depositAmount, status, outbound, inbound }) => {
  const enableThroughFaresOnHoldOption = config('ENABLE_THROUGH_FARES_ON_HOLD', { canOverride: true }) === 'true';
  const hasThroughOrSummatedFares =
    isThroughFareOrSummatedFares({ direction: outbound }) || isThroughFareOrSummatedFares({ direction: inbound });
  const hasPositiveDeposit = depositAmount !== null && depositAmount >= 0;
  const hasDeposit = depositAmount !== null && depositAmount > 0;
  const areAllLegsThroughFare = [...outbound, ...inbound].every((leg) => leg.isThroughFare);
  const areAllLegsESOrER = [...outbound, ...inbound].every(
    (leg) => leg.serviceType.code === serviceTypeCodes.EUROSTAR || leg.serviceType.code === serviceTypeCodes.GREENSPEED
  );
  const meetsThroughFareESER = areAllLegsThroughFare && areAllLegsESOrER;
  const filteredPaymentTypes = [{ id: 'payBalance', price: totalPriceToBePaid, type: paymentTypes.PAY_BALANCE }];

  if (
    !enableThroughFaresOnHoldOption
      ? !hasThroughOrSummatedFares && hasDeposit
      : (!hasThroughOrSummatedFares && hasDeposit) || (meetsThroughFareESER && hasDeposit)
  ) {
    filteredPaymentTypes.push({
      id: 'onHold',
      price: depositAmount,
      type: paymentTypes.ON_HOLD_WITH_DEPOSIT
    });
  }

  if (
    !enableThroughFaresOnHoldOption
      ? !hasThroughOrSummatedFares && hasPositiveDeposit && status !== bookingStatus.TEMPORARILY_ON_HOLD
      : (!hasThroughOrSummatedFares && hasPositiveDeposit && status !== bookingStatus.TEMPORARILY_ON_HOLD) ||
        (meetsThroughFareESER && hasPositiveDeposit && status !== bookingStatus.TEMPORARILY_ON_HOLD)
  ) {
    filteredPaymentTypes.push({
      id: 'onHold',
      price: 0,
      type: paymentTypes.ON_HOLD_NO_DEPOSIT
    });
  }

  return filteredPaymentTypes;
};

const isVoucherPayment = (payment) => payment.method === codes.VOUCHER || payment.method === codes.EVOUCHER;

const isPrrRefundPayment = (payment) => payment.reference && payment.reference.startsWith('prr-');

const isSubscriptionOnly = (booking) => {
  const subscriptionList = [productFamilyCodes.THPASS];

  return subscriptionList.includes(booking.productFamilyGroup) && !booking.pendingCreationLegs?.length;
};

const nonCancelledExtras = (extras) => extras?.filter((e) => !e.cancelled);
const totalExtras = (extras) => extras?.reduce((acc, item) => acc + item.price, 0);

const isExtraOnly = (outbound = [], inbound = [], outboundExtras = [], inboundExtras = []) => {
  const isTrainBooking = outbound.length > 0 || inbound.length > 0;
  const isExtras =
    (outboundExtras.length > 0 && outboundExtras.every(({ type }) => type !== productTypes.HOTEL)) ||
    (inboundExtras.length > 0 && inboundExtras.every(({ type }) => type !== productTypes.HOTEL));

  return !isTrainBooking && isExtras;
};

export {
  generateMerchantReference,
  getAvailablePaymentTypes,
  isVoucherPayment,
  isPrrRefundPayment,
  isSubscriptionOnly,
  isExtraOnly,
  nonCancelledExtras,
  totalExtras
};
